import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import CurrencyInput from 'react-currency-input-field';
import { useDispatch, useSelector } from 'react-redux';

import 'react-datepicker/dist/react-datepicker.css';

import { editarPago } from '../../../../actions/pagosProyectos';
import { listarMetodosPago } from '../../../../actions/configuracion';

const formatYYYYMMDD = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const parseFecha = (value) => {
    if (!value) return new Date();
    const d = new Date(value);
    return isNaN(d.getTime()) ? new Date() : d;
};

const EditarPagoModal = ({ show, onHide, onSuccess, idProyecto, pago }) => {
    const dispatch = useDispatch();

    const { metodos_pago } = useSelector(state => state.configuracion);

    const [fecha, setFecha] = useState(new Date());
    const [monto, setMonto] = useState('');
    const [metodoSel, setMetodoSel] = useState(null);
    const [referencia, setReferencia] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!show) return;

        if (!metodos_pago?.length) {
            dispatch(listarMetodosPago());
        }
    }, [show, metodos_pago, dispatch]);

    useEffect(() => {
        if (!show || !pago) return;

        setFecha(parseFecha(pago?.fecha));
        setMonto(pago?.monto ?? '');
        setMetodoSel(pago?.metodo_pago?._id ? { value: pago.metodo_pago._id, label: pago.metodo_pago.nombre } : null);
        setReferencia(pago?.referencia || '');
        setObservaciones(pago?.observaciones || '');
    }, [show, pago]);

    useEffect(() => {
        if (show) return;

        setFecha(new Date());
        setMonto('');
        setMetodoSel(null);
        setReferencia('');
        setObservaciones('');
        setGuardando(false);
    }, [show]);

    const opcionesMetodos = useMemo(() => {
        return (metodos_pago || []).map(m => ({ value: m._id, label: m.nombre }));
    }, [metodos_pago]);

    const puedeGuardar = useMemo(() => {
        return Boolean(Number(monto) > 0 && metodoSel?.value && !guardando);
    }, [monto, metodoSel, guardando]);

    const onGuardar = async () => {
        if (!puedeGuardar || !pago?._id) return;

        setGuardando(true);
        const ok = await dispatch(editarPago({
            id: pago._id,
            id_proyecto: idProyecto,
            fecha: formatYYYYMMDD(fecha),
            monto: Number(monto),
            metodo_pago: metodoSel.value,
            referencia: referencia.trim() || null,
            observaciones: observaciones.trim() || null
        }));
        setGuardando(false);

        if (!ok) return;

        if (typeof onSuccess === 'function') {
            onSuccess();
        } else if (typeof onHide === 'function') {
            onHide();
        }
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>Editar pago</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="row">
                    <div className="col-6 mb-3">
                        <div className="text-muted small mb-1">Fecha</div>
                        <DatePicker
                            selected={fecha}
                            onChange={(d) => setFecha(d)}
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <div className="text-muted small mb-1">Monto</div>
                        <CurrencyInput
                            value={monto}
                            decimalsLimit={3}
                            allowNegativeValue={false}
                            placeholder="$ Monto"
                            className="form-control"
                            prefix="$ "
                            groupSeparator=","
                            decimalSeparator="."
                            onValueChange={(value) => setMonto(value || '')}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <div className="text-muted small mb-1">Método de pago</div>
                    <Select
                        options={opcionesMetodos}
                        value={metodoSel}
                        onChange={setMetodoSel}
                        placeholder="Método de pago..."
                        noOptionsMessage={() => 'Sin resultados'}
                    />
                </div>

                <div className="mb-3">
                    <div className="text-muted small mb-1">Referencia (opcional)</div>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Referencia (opcional)"
                        value={referencia}
                        onChange={(e) => setReferencia(e.target.value)}
                    />
                </div>

                <div className="mb-3">
                    <div className="text-muted small mb-1">Observaciones (opcional)</div>
                    <textarea
                        className="form-control"
                        placeholder="Observaciones (opcional)"
                        rows={3}
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                    />
                </div>
            </Modal.Body>

            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onHide} disabled={guardando}>
                    Cancelar
                </button>
                <button type="button" className="btn btn-primary" onClick={onGuardar} disabled={!puedeGuardar}>
                    Guardar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditarPagoModal;
